/**
 * InstructionStepTwo Component - Topic Familiarity Survey
 *
 * Third step in the intro flow - assesses participant's baseline familiarity with the topic domain.
 * This data helps analyze whether prior topic knowledge affects learning outcomes.
 *
 * Survey Questions:
 * Questions are loaded from the game configuration based on the treatment.
 * Each topic has its own familiarity questions about the subject matter.
 *
 * Data Storage:
 * Answers saved to player.set("topic_familiarity_survey", answers)
 *
 * Validation:
 * - All questions must be answered before proceeding
 * - Shows error message if incomplete
 * - Auto-progresses to game after successful submission
 */

import React, { useState } from "react";
import { usePlayer, useGame } from "@empirica/core/player/classic/react";

// Default questions if config doesn't provide familiarity questions
// NOTE: must carry a category that the render filters below recognize —
// an uncategorized fallback question is never rendered, yet used to count
// toward validation, deadlocking the participant on an empty survey.
const defaultSurveyQuestions = [
  {
    id: 1,
    question: "How familiar are you with this topic before starting the experiment?",
    name: "topic_familiarity",
    category: "Topic-specific",
    options: [
      "1 (Not at all familiar)",
      "2", "3", "4", "5", "6",
      "7 (Very familiar)"
    ]
  }
];

export function InstructionStepTwo({ next }) {
  const player = usePlayer();
  const game = useGame();

  // Try to get config from game (will be undefined during intro, which is expected)
  const config = game?.get ? game.get("config") : null;

  const topicName = config?.name || "the topic";
  const surveyQuestions = config?.familiarityQuestions || defaultSurveyQuestions;
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [allCorrect, setAllCorrect] = useState(false);

  // Split questions into two blocks (also used for validation below)
  // Categories: Domain-specific (FQ1-2), Topic-specific (FQ3-4), AI Self-Efficacy (FQ5-7)
  const knowledgeQuestions = surveyQuestions.filter(q =>
    q.category === 'Domain-specific' || q.category === 'Topic-specific'
  );
  const agreementQuestions = surveyQuestions.filter(q =>
    q.category === 'AI Self-Efficacy'
  );
  const renderedQuestions = [...knowledgeQuestions, ...agreementQuestions];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setAnswers(prevState => {
      const newAnswers = { ...prevState, [name]: value };
      // Auto-save partial answers so a timer expiry doesn't lose the survey
      player.set("topic_familiarity_survey", newAnswers);
      player.set("topic_familiarity_survey_partial", true);
      return newAnswers;
    });
    setSubmitted(false);
    setAllCorrect(false);
  };

  const handleSubmit = () => {
    // Validate against the questions actually RENDERED (categorized), not the
    // raw config list — a question with an unrecognized category is hidden and
    // must not block submission
    const allAnswered = renderedQuestions.every(q => answers[q.name] !== undefined);

    if (allAnswered) {
      // Save answers to player object
      player.set("topic_familiarity_survey", answers);
      player.set("topic_familiarity_survey_partial", false);

      // Save question metadata for analysis (category)
      const questionMetadata = surveyQuestions.reduce((acc, q) => {
        acc[q.id] = {
          category: q.category || ''
        };
        return acc;
      }, {});
      player.set("topic_familiarity_survey_metadata", questionMetadata);

      setSubmitted(true);
      setAllCorrect(true);
      // Auto-progress to next step
      next();
    } else {
      // Show error message
      setSubmitted(true);
      setAllCorrect(false);
    }
  };

  // Helper to render text with quoted content in bold
  const renderTextWithBold = (text) => {
    // Split by quoted text (matches "..." patterns)
    const parts = text.split(/(".*?")/g);
    return parts.map((part, index) => {
      if (part.startsWith('"') && part.endsWith('"')) {
        // Remove quotes and render bold
        return <strong key={index}>{part.slice(1, -1)}</strong>;
      }
      return part;
    });
  };

  const renderQuestion = (q) => {
    const currentAnswer = answers[q.name];

    // Extract low/high labels from options (e.g., "1 (Not at all familiar)" -> "Not at all familiar")
    const firstOption = q.options[0] || "";
    const lastOption = q.options[q.options.length - 1] || "";
    const lowLabel = firstOption.match(/\(([^)]+)\)/)?.[1] || "Low";
    const highLabel = lastOption.match(/\(([^)]+)\)/)?.[1] || "High";

    return (
      <div key={q.id} className="mb-4 p-4 border rounded-lg shadow-sm">
        <h3 className="text-lg mb-3">{q.id}. {renderTextWithBold(q.question)}</h3>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500 w-28 text-right">{lowLabel}</span>
          <div className="flex gap-1 flex-1 justify-center">
            {[0, 1, 2, 3, 4, 5, 6].map((v) => {
              const optionValue = q.options[v] || String(v);
              return (
                <button
                  key={v}
                  type="button"
                  onClick={() => {
                    if (!(submitted && allCorrect)) {
                      handleChange({ target: { name: q.name, value: optionValue } });
                    }
                  }}
                  disabled={submitted && allCorrect}
                  className={`w-10 h-10 rounded-lg font-medium transition-all ${
                    currentAnswer === optionValue
                      ? 'bg-blue-600 text-white shadow-md scale-110'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  } ${submitted && allCorrect ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {v}
                </button>
              );
            })}
          </div>
          <span className="text-xs text-gray-500 w-28">{highLabel}</span>
        </div>
      </div>
    );
  };

  return (
    <div className="h-full overflow-y-auto p-6">
      <div className="max-w-4xl mx-auto">
      <div className="bg-white shadow-xl rounded-lg p-6">
        <h1 className="text-3xl font-bold mb-6 text-gray-800">Background Survey</h1>
        <p className="mb-6 text-lg text-gray-700 leading-relaxed">
          Before we begin, please answer a few questions about your background knowledge.
          Your specific topic will be revealed shortly.
        </p>

        {/* Block 1: Knowledge Questions */}
        {knowledgeQuestions.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-semibold mb-4 text-gray-700 border-b pb-2">
              Section 1: Topic Knowledge
            </h2>
            <div className="space-y-6">
              {knowledgeQuestions.map(renderQuestion)}
            </div>
          </div>
        )}

        {/* Block 2: Agreement Questions */}
        {agreementQuestions.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-semibold mb-4 text-gray-700 border-b pb-2">
              Section 2: AI Experience
            </h2>
            <p className="mb-4 text-gray-600">
              How much do you agree with the following statements?
            </p>
            <div className="space-y-6">
              {agreementQuestions.map(renderQuestion)}
            </div>
          </div>
        )}

        <div className="mt-8 border-t pt-4 flex justify-end items-center">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitted && allCorrect}
            className="px-6 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50 transition duration-150"
          >
            {submitted && allCorrect ? "Submitted" : "Submit and Continue"}
          </button>
        </div>

        {submitted && !allCorrect && (
          <p className="mt-4 text-sm font-medium text-red-600">
            Please answer all questions before proceeding.
          </p>
        )}
      </div>
      </div>
    </div>
  );
}